import { useRef } from 'react';
import { Download, Award, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getBadgeInfo } from '@/data/badgeCatalog';
import { isCertifiableBadge } from '@/utils/certificateGenerator';

// Renders a beautiful badge and provides a PNG download function.
const BadgeCard = ({ badge, learnerName, earned = true }) => {
  const canvasRef = useRef(null);
  const info = getBadgeInfo(badge.badge_id || badge.id);

  const drawBadge = () => {
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');

    // Background - dark radial
    const bgGrad = ctx.createRadialGradient(400, 400, 100, 400, 400, 500);
    bgGrad.addColorStop(0, '#1a1a20');
    bgGrad.addColorStop(1, '#050505');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 800, 800);

    // Outer glow ring
    const glowGrad = ctx.createRadialGradient(400, 400, 220, 400, 400, 320);
    glowGrad.addColorStop(0, info.color + '00');
    glowGrad.addColorStop(0.5, info.color + '55');
    glowGrad.addColorStop(1, info.color + '00');
    ctx.fillStyle = glowGrad;
    ctx.fillRect(0, 0, 800, 800);

    // Big circle
    const circleGrad = ctx.createRadialGradient(400, 350, 50, 400, 400, 260);
    circleGrad.addColorStop(0, info.accent);
    circleGrad.addColorStop(1, info.color);
    ctx.fillStyle = circleGrad;
    ctx.beginPath();
    ctx.arc(400, 400, 240, 0, Math.PI * 2);
    ctx.fill();

    // Inner border
    ctx.strokeStyle = '#ffffff33';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(400, 400, 240, 0, Math.PI * 2);
    ctx.stroke();

    // Inner circle darker
    ctx.fillStyle = '#0a0a0a';
    ctx.beginPath();
    ctx.arc(400, 400, 200, 0, Math.PI * 2);
    ctx.fill();

    // Icon (large emoji)
    ctx.font = '180px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(info.icon, 400, 380);

    // Tier ribbon
    ctx.fillStyle = info.color;
    ctx.fillRect(280, 500, 240, 40);
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText(info.tier.toUpperCase(), 400, 520);

    // Badge Name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 42px sans-serif';
    ctx.fillText(info.name, 400, 600);

    // Description
    ctx.fillStyle = '#a1a1aa';
    ctx.font = '20px sans-serif';
    ctx.fillText(info.description, 400, 640);

    // Awarded to
    if (learnerName) {
      ctx.fillStyle = info.accent;
      ctx.font = 'italic 22px sans-serif';
      ctx.fillText('Awarded to ' + learnerName, 400, 700);
    }

    // Footer
    ctx.fillStyle = '#71717a';
    ctx.font = '16px sans-serif';
    ctx.fillText('DSA With Kapil · Visual Learning Lab', 400, 760);

    return canvas;
  };

  const handleDownload = () => {
    const canvas = drawBadge();
    const link = document.createElement('a');
    link.download = `badge-${info.id}-${(learnerName || 'learner').replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div 
      data-testid={`badge-card-${info.id}`}
      className={`bg-[#0F0F12] border rounded-xl p-6 transition-all ${
        earned ? 'border-white/10 hover:border-white/30' : 'border-white/5 opacity-40'
      }`}
    >
      {/* Visual Badge */}
      <div className="relative w-full aspect-square rounded-lg overflow-hidden mb-4 flex items-center justify-center"
        style={{ 
          background: `radial-gradient(circle at center, ${info.color}22 0%, transparent 70%), #0a0a0a`
        }}
      >
        <div className="absolute inset-6 rounded-full"
          style={{
            background: `radial-gradient(circle at 50% 40%, ${info.accent}, ${info.color})`,
            boxShadow: earned ? `0 0 30px ${info.color}88` : 'none'
          }}
        />
        <div className="absolute inset-10 rounded-full bg-[#050505] flex flex-col items-center justify-center">
          <div className="text-6xl mb-1" style={{ filter: earned ? 'none' : 'grayscale(1)' }}>
            {info.icon}
          </div>
          <div className="text-[10px] font-code font-bold uppercase tracking-widest text-white/70">
            {info.tier}
          </div>
        </div>
      </div>

      {/* Info */}
      <h3 className="font-heading text-lg font-semibold text-white mb-1 flex items-center gap-2">
        {info.name}
        {earned && isCertifiableBadge(info.id) && (
          <span title="Certificate available" className="text-[10px] font-code font-bold px-1.5 py-0.5 rounded bg-[#FFB800]/20 text-[#FFB800] border border-[#FFB800]/40 flex items-center gap-1">
            <FileDown className="w-2.5 h-2.5" />
            CERT
          </span>
        )}
      </h3>
      <p className="text-[#A1A1AA] text-sm font-body mb-4 min-h-[40px]">{info.description}</p>

      {earned ? (
        <Button
          data-testid={`badge-download-${info.id}`}
          onClick={handleDownload}
          className="w-full font-heading font-semibold text-sm"
          style={{ background: info.color, color: '#000' }}
        >
          <Download className="w-4 h-4 mr-2" />
          Download PNG
        </Button>
      ) : (
        <div className="w-full text-center py-2 rounded border border-white/10 text-[#71717A] text-xs font-code">
          🔒 LOCKED
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default BadgeCard;
